'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import {
  Target, Loader2, RefreshCw, ArrowUp, ArrowDown, AlertTriangle, AlertOctagon,
  Info, CheckCircle2, Lightbulb, ChevronDown,
} from 'lucide-react';
import type { Audit, Severity, EntityRow, InsightsBundle, ConversionStat } from '@/lib/ads-types';
import {
  fmtNum, fmtMoney, SmartInsights, TrendCharts, BreakdownPanel,
} from '@/components/ads/AdsAnalytics';
import { TrackerGrid } from '@/components/ads/TrackerGrid';
import { AdsAssistant } from '@/components/ads/AdsAssistant';

const RANGES: { id: string; label: string }[] = [
  { id: 'last_7d', label: 'Last 7 days' },
  { id: 'last_14d', label: 'Last 14 days' },
  { id: 'last_30d', label: 'Last 30 days' },
  { id: 'last_90d', label: 'Last 90 days' },
  { id: 'maximum', label: 'Maximum (all time)' },
  { id: 'custom', label: 'Custom range…' },
];

const RANGE_LABEL: Record<string, string> = {
  last_7d: 'Last 7 days', last_14d: 'Last 14 days', last_30d: 'Last 30 days', last_90d: 'Last 90 days',
  maximum: 'Maximum (all time)',
};

// Persist the chosen window across reloads (global, not per-account).
const LS_KEY = 'beast.ads.window';
function loadWindow(): { range: string; since: string; until: string } {
  if (typeof window === 'undefined') return { range: 'last_30d', since: '', until: '' };
  try {
    const w = JSON.parse(localStorage.getItem(LS_KEY) || '{}');
    return { range: w.range || 'last_30d', since: w.since || '', until: w.until || '' };
  } catch {
    return { range: 'last_30d', since: '', until: '' };
  }
}

function isoDaysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

interface AccountRow {
  id: string; name: string; currency: string; business_name?: string;
  lastAudit: string | null; lastScore: number | null;
}

type Tab = 'overview' | 'trends' | 'audience' | 'delivery' | 'campaigns' | 'tracker' | 'assistant';
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'trends', label: 'Trends' },
  { id: 'audience', label: 'Audience' },
  { id: 'delivery', label: 'Delivery' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'tracker', label: 'Tracker' },
  { id: 'assistant', label: 'Assistant' },
];

// ---- helpers ----
function scoreColor(s: number): string {
  if (s >= 80) return 'text-green-400';
  if (s >= 60) return 'text-cyan-400';
  if (s >= 40) return 'text-amber-400';
  return 'text-red-400';
}
function scoreRing(s: number): string {
  if (s >= 80) return 'rgb(74 222 128)';
  if (s >= 60) return 'rgb(34 211 238)';
  if (s >= 40) return 'rgb(251 191 36)';
  return 'rgb(248 113 113)';
}

function TrendChip({ value, goodWhenDown = false }: { value: number; goodWhenDown?: boolean }) {
  if (!value || Math.abs(value) < 0.5) return <span className="text-gray-600 text-xs">—</span>;
  const up = value > 0;
  const good = goodWhenDown ? !up : up;
  return (
    <span className={`flex items-center gap-0.5 text-xs ${good ? 'text-green-400' : 'text-red-400'}`}>
      {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
      {Math.abs(Math.round(value))}%
    </span>
  );
}

function MetricCard({ label, value, trend, goodWhenDown }: { label: string; value: string; trend?: number; goodWhenDown?: boolean }) {
  return (
    <div className="glass-card p-4">
      <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-1">{label}</p>
      <div className="flex items-end justify-between gap-2">
        <span className="text-xl font-bold text-white">{value}</span>
        {trend !== undefined && <TrendChip value={trend} goodWhenDown={goodWhenDown} />}
      </div>
    </div>
  );
}

const FSEV: Record<Severity, { icon: typeof Info; color: string; border: string }> = {
  critical: { icon: AlertOctagon, color: 'text-red-400', border: 'border-l-red-500' },
  warning: { icon: AlertTriangle, color: 'text-amber-400', border: 'border-l-amber-500' },
  info: { icon: Info, color: 'text-blue-400', border: 'border-l-blue-500' },
  positive: { icon: CheckCircle2, color: 'text-green-400', border: 'border-l-green-500' },
};

function EntityTable({ title, rows, cur, conv }: { title: string; rows: EntityRow[]; cur: string; conv?: string }) {
  if (!rows?.length) return null;
  const maxShare = Math.max(...rows.map(r => r.spendShare || 0), 1);
  const showConv = !!conv && rows.some(r => (r.convCount || 0) > 0);
  return (
    <div className="glass-card overflow-hidden">
      <div className="px-4 py-3 border-b border-white/[0.04] flex items-center justify-between">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
        <span className="text-[10px] text-gray-500">{rows.length}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] text-gray-500 uppercase">
              <th className="text-left font-medium px-4 py-2">Name</th>
              <th className="text-right font-medium px-3 py-2">Spend</th>
              <th className="text-right font-medium px-3 py-2">Share</th>
              <th className="text-right font-medium px-3 py-2">Impr.</th>
              <th className="text-right font-medium px-3 py-2">Clicks</th>
              <th className="text-right font-medium px-3 py-2">CTR</th>
              <th className="text-right font-medium px-3 py-2">CPC</th>
              <th className={`text-right font-medium px-3 py-2 ${showConv ? '' : 'pr-4'}`}>CPM</th>
              {showConv && <th className="text-right font-medium px-3 py-2 text-cyan-500" title={conv}>{conv}</th>}
              {showConv && <th className="text-right font-medium px-4 py-2 text-cyan-500">Cost/Res.</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-t border-white/[0.04] hover:bg-white/[0.02]">
                <td className="px-4 py-2.5 max-w-[260px]">
                  <div className="truncate text-gray-200" title={r.name}>{r.name || '—'}</div>
                  {r.objective && <div className="text-[10px] text-gray-600">{r.objective}</div>}
                </td>
                <td className="px-3 py-2.5 text-right text-gray-300 whitespace-nowrap">{fmtMoney(r.spend, cur)}</td>
                <td className="px-3 py-2.5 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <div className="w-12 h-1.5 rounded-full bg-gray-800 overflow-hidden">
                      <div className="h-full bg-cyan-500/70" style={{ width: `${((r.spendShare || 0) / maxShare) * 100}%` }} />
                    </div>
                    <span className="text-gray-400 text-xs w-9 text-right">{r.spendShare ?? 0}%</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right text-gray-400">{fmtNum(r.impressions)}</td>
                <td className="px-3 py-2.5 text-right text-gray-400">{fmtNum(r.clicks)}</td>
                <td className="px-3 py-2.5 text-right text-cyan-400 font-medium">{r.ctr.toFixed(2)}%</td>
                <td className="px-3 py-2.5 text-right text-gray-300">{fmtMoney(r.cpc, cur)}</td>
                <td className={`py-2.5 text-right text-gray-400 ${showConv ? 'px-3' : 'px-4'}`}>{fmtMoney(r.cpm, cur)}</td>
                {showConv && <td className="px-3 py-2.5 text-right text-gray-200 font-medium">{r.convCount ? fmtNum(r.convCount) : '—'}</td>}
                {showConv && <td className="px-4 py-2.5 text-right text-gray-300">{r.convCostPer ? fmtMoney(r.convCostPer, cur) : '—'}</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ConversionsPanel({ conversions, primaryId, cur }: { conversions: ConversionStat[]; primaryId?: string; cur: string }) {
  return (
    <div className="glass-card p-5">
      <h3 className="text-sm font-semibold text-white mb-3">Conversions <span className="text-gray-600 font-normal text-xs">· results this period</span></h3>
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
        {conversions.map(c => {
          const isPrimary = c.id === primaryId;
          return (
            <div key={c.id} className={`rounded-lg border p-3 ${isPrimary ? 'border-cyan-500/40 bg-cyan-500/[0.05]' : 'border-white/[0.08] bg-[rgb(var(--background))]/40'}`}>
              <div className="flex items-center justify-between mb-1 gap-1">
                <span className="text-xs text-gray-400 truncate" title={c.name}>{c.name}</span>
                {c.isCustom && <span className="text-[8px] uppercase px-1 py-0.5 rounded bg-purple-500/15 text-purple-300 shrink-0">custom</span>}
              </div>
              <div className="text-xl font-bold text-white">{fmtNum(c.count)}</div>
              <p className="text-[11px] text-gray-500 mt-0.5">{fmtMoney(c.costPer, cur)} / result</p>
              {c.value ? <p className="text-[10px] text-green-400 mt-0.5">{fmtMoney(c.value, cur)} value</p> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BundlePending({ loading, hasToken }: { loading: boolean; hasToken: boolean }) {
  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      {loading ? (
        <>
          <Loader2 className="h-7 w-7 animate-spin text-cyan-400 mb-3" />
          <p className="text-sm text-gray-500">Pulling live breakdowns from Meta…</p>
        </>
      ) : (
        <p className="text-sm text-gray-500 max-w-md">
          {hasToken ? 'No breakdown data available for this account/period.' : 'Live breakdowns need META_ACCESS_TOKEN.'}
        </p>
      )}
    </div>
  );
}

// ---- page ----
/** Global: all ad accounts. Inside a project tab, `onlyAccountId` HARD-scopes
 *  to that project's account — Olga must never see AstroForYou campaigns. */
export default function AdsPage({ onlyAccountId }: { onlyAccountId?: string } = {}) {
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selected, setSelected] = useState<string>('');
  const [audit, setAudit] = useState<Audit | null>(null);
  const [bundle, setBundle] = useState<InsightsBundle | null>(null);
  const [liveRefresh, setLiveRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [bundleLoading, setBundleLoading] = useState(false);
  const [noAudit, setNoAudit] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  // Start from the SSR default (never read localStorage during render) so the
  // server and client first render match — reading the saved window here would
  // make isCustom differ between server/client and break hydration. The saved
  // window is restored in the mount effect below, after hydration.
  const [range, setRange] = useState<string>('last_30d');
  const [customSince, setCustomSince] = useState<string>('');
  const [customUntil, setCustomUntil] = useState<string>('');

  // Restore the persisted window once, after mount (post-hydration).
  useEffect(() => {
    const w = loadWindow();
    setRange(w.range);
    setCustomSince(w.since);
    setCustomUntil(w.until);
  }, []);

  // Save the window whenever it changes so the next load restores it. Skip the
  // first run so we don't clobber the saved value with the SSR default before
  // the restore effect above has applied it.
  const persistReady = useRef(false);
  useEffect(() => {
    if (!persistReady.current) { persistReady.current = true; return; }
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ range, since: customSince, until: customUntil }));
    } catch { /* storage may be unavailable — non-fatal */ }
  }, [range, customSince, customUntil]);
  const [campaigns, setCampaigns] = useState<Array<{ id: string; name: string; status: string }>>([]);
  const [campaign, setCampaign] = useState<string>('__ALL__');

  useEffect(() => {
    fetch('/api/ads/accounts')
      .then(r => r.json())
      .then(d => {
        let accs: AccountRow[] = d.accounts || [];
        // HARD scope: project tab passes onlyAccountId — hide every other account
        if (onlyAccountId) accs = accs.filter(a => a.id === onlyAccountId);
        setAccounts(accs);
        setLiveRefresh(!!d.liveRefresh);
        // ?account=<id> scopes the page to a project's ad account (set by /p/[slug] tabs)
        const urlAccount = typeof window !== 'undefined'
          ? new URLSearchParams(window.location.search).get('account') : null;
        const scoped = urlAccount && accs.find(a => a.id === urlAccount);
        const withAudit = scoped || accs.find(a => a.lastAudit) || accs[0];
        if (withAudit) setSelected(withAudit.id);
        else setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  }, []);

  // load the account's campaigns for the per-campaign selector; reset to All on account switch
  useEffect(() => {
    if (!selected) { setCampaigns([]); return; }
    setCampaign('__ALL__');
    fetch(`/api/ads/campaigns/${selected}`)
      .then(r => r.json())
      .then(d => setCampaigns(d.campaigns || []))
      .catch(() => setCampaigns([]));
  }, [selected]);

  // build the query for the active window (named preset OR custom since/until)
  const isCustom = range === 'custom';
  const customReady = isCustom && !!customSince && !!customUntil && customSince <= customUntil;
  const campaignQuery = campaign !== '__ALL__' ? `&campaign=${campaign}` : '';
  const winQuery = (isCustom ? `since=${customSince}&until=${customUntil}` : `range=${range}`) + campaignQuery;

  // Stale-response guards: several loads can be in flight at once (account
  // switch, window change, manual Refresh). Only the NEWEST request may write
  // state — otherwise a slow older GET resolves last and overwrites fresh
  // insights with the previous snapshot (Boss 2026-07-09: "they get refreshed,
  // then overwritten by the previous ones").
  const bundleSeq = useRef(0);
  const auditSeq = useRef(0);

  const loadBundle = useCallback((id: string, qs: string) => {
    const seq = ++bundleSeq.current;
    setBundleLoading(true); setBundle(null);
    fetch(`/api/ads/insights/${id}?${qs}`)
      .then(async r => ({ ok: r.ok, body: await r.json() }))
      .then(({ ok, body }) => { if (seq === bundleSeq.current && ok) setBundle(body.bundle); })
      .catch(() => { /* breakdowns are best-effort */ })
      .finally(() => { if (seq === bundleSeq.current) setBundleLoading(false); });
  }, []);

  const loadAudit = useCallback((id: string, qs: string) => {
    const seq = ++auditSeq.current;
    setLoading(true); setNoAudit(false); setError(null);
    fetch(`/api/ads/audit/${id}?${qs}`)
      .then(async r => ({ ok: r.ok, status: r.status, body: await r.json() }))
      .then(({ ok, status, body }) => {
        if (seq !== auditSeq.current) return; // a newer request owns the state now
        if (ok) setAudit(body.audit);
        else if (status === 404) { setAudit(null); setNoAudit(true); }
        else setError(body.message || body.error || 'Failed to load audit');
        setLoading(false);
      })
      .catch(e => { if (seq === auditSeq.current) { setError(e.message); setLoading(false); } });
  }, []);

  useEffect(() => { setTab('overview'); }, [selected]);
  useEffect(() => {
    if (!selected) return;
    if (isCustom && !customReady) return; // wait until both custom dates are set
    loadAudit(selected, winQuery);
    loadBundle(selected, winQuery);
  }, [selected, winQuery, isCustom, customReady, loadAudit, loadBundle, campaign]);

  const refresh = () => {
    if (!selected || (isCustom && !customReady)) return;
    setRefreshing(true);
    // invalidate every in-flight GET — the fresh Meta pull owns the state now
    const seq = ++auditSeq.current;
    ++bundleSeq.current;
    fetch(`/api/ads/audit/${selected}?${winQuery}`, { method: 'POST' })
      .then(async r => ({ ok: r.ok, body: await r.json() }))
      .then(({ ok, body }) => {
        if (seq !== auditSeq.current) return; // user switched account/window mid-refresh
        if (ok) { setAudit(body.audit); setNoAudit(false); setLoading(false); loadBundle(selected, winQuery); toast.success('Refreshed from Meta'); }
        else toast.error(body.message || 'Refresh failed', { duration: 8000 });
      })
      .catch(e => toast.error(e.message))
      .finally(() => setRefreshing(false));
  };

  const acct = accounts.find(a => a.id === selected);
  const cur = audit?.account.currency || acct?.currency || '';

  return (
    <div className="p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600">
            <Target className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Beast Ads</h1>
            <p className="text-xs text-gray-500">Meta campaign performance · {accounts.length} accounts</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={selected}
              onChange={e => setSelected(e.target.value)}
              className="appearance-none bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer max-w-[260px]"
            >
              {accounts.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name || a.id}{a.lastScore != null ? `  ·  ${Math.round(a.lastScore)}` : ''}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
          {campaigns.length > 0 && (
            <div className="relative">
              <select
                value={campaign}
                onChange={e => setCampaign(e.target.value)}
                title="Scope analytics + tracker to one campaign"
                className="appearance-none bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer max-w-[240px]"
              >
                <option value="__ALL__">All campaigns</option>
                {campaigns.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
            </div>
          )}
          <div className="relative">
            <select
              value={range}
              onChange={e => {
                const v = e.target.value;
                setRange(v);
                if (v === 'custom' && !(customSince && customUntil)) {
                  setCustomSince(isoDaysAgo(7));
                  setCustomUntil(isoDaysAgo(1));
                }
              }}
              className="appearance-none bg-[#0d1117]/85 border border-white/[0.08] rounded-lg pl-3 pr-9 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 cursor-pointer"
            >
              {RANGES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>
          {isCustom && (
            <div className="flex items-center gap-1.5">
              <input
                type="date"
                value={customSince}
                max={customUntil || isoDaysAgo(1)}
                onChange={e => setCustomSince(e.target.value)}
                className="bg-[#0d1117]/85 border border-white/[0.08] rounded-lg px-2.5 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 [color-scheme:dark]"
              />
              <span className="text-gray-600 text-xs">→</span>
              <input
                type="date"
                value={customUntil}
                min={customSince}
                max={isoDaysAgo(1)}
                onChange={e => setCustomUntil(e.target.value)}
                className="bg-[#0d1117]/85 border border-white/[0.08] rounded-lg px-2.5 py-2 text-sm text-white focus:outline-none focus:border-cyan-500/50 [color-scheme:dark]"
              />
            </div>
          )}
          <button
            onClick={refresh}
            disabled={refreshing || !selected}
            title={liveRefresh ? 'Refresh from Meta Graph API' : 'Live refresh needs META_ACCESS_TOKEN'}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-white/[0.08] bg-white/[0.05] text-sm text-gray-300 hover:text-white hover:border-cyan-500/40 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-white/[0.04] mb-6 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ${
              tab === t.id ? 'border-cyan-400 text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-300'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center h-80">
          <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
        </div>
      )}

      {!loading && error && (
        <div className="p-6 rounded-xl border border-red-500/20 bg-red-500/5">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {!loading && noAudit && tab !== 'assistant' && (
        <div className="text-center py-20 rounded-xl border border-white/[0.08] bg-white/[0.04]">
          <Target className="h-12 w-12 mx-auto text-gray-700 mb-4" />
          <p className="text-gray-300 font-medium mb-1">No audit yet for {acct?.name || 'this account'}</p>
          <p className="text-xs text-gray-500 max-w-md mx-auto">
            {liveRefresh ? 'Click Refresh to generate one from the Meta Graph API.' : 'This account has no saved audit yet.'}
          </p>
        </div>
      )}

      {!loading && audit && (
        <>
          {/* ===== OVERVIEW ===== */}
          {tab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="glass-card p-5 flex items-center gap-5">
                  <div
                    className="relative h-28 w-28 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: `conic-gradient(${scoreRing(audit.score)} ${audit.score * 3.6}deg, rgb(31 41 55) 0deg)` }}
                  >
                    <div className="h-20 w-20 rounded-full bg-[#0d1117]/85 flex flex-col items-center justify-center">
                      <span className={`text-2xl font-bold ${scoreColor(audit.score)}`}>{Math.round(audit.score)}</span>
                      <span className="text-[9px] text-gray-500">/ 100</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white mb-0.5">Audit score</p>
                    <p className="text-xs text-gray-500 mb-2">{RANGE_LABEL[audit.period.preset || ''] || `${audit.period.since} → ${audit.period.until}`}</p>
                    <span className="inline-block text-[10px] px-2 py-0.5 rounded-full border border-gray-700 text-gray-400">
                      {audit.source === 'graph-live' ? 'Live · Graph API' : 'Snapshot · MCP'}
                    </span>
                  </div>
                </div>

                <div className="lg:col-span-2 glass-card p-5">
                  <p className="text-sm font-semibold text-white mb-3">Score breakdown</p>
                  <div className="space-y-2.5">
                    {audit.scoreParts.map(p => (
                      <div key={p.label}>
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-gray-300">{p.label} <span className="text-gray-600">· {p.weight}%</span></span>
                          <span className={scoreColor(p.score)}>{Math.round(p.score)}{p.note && <span className="text-gray-600 font-normal"> · {p.note}</span>}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-800 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${p.score}%`, background: scoreRing(p.score) }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
                <MetricCard label="Spend" value={fmtMoney(audit.totals.spend, cur)} trend={audit.trend.spend} />
                <MetricCard label="Impr." value={fmtNum(audit.totals.impressions)} trend={audit.trend.impressions} />
                <MetricCard label="Reach" value={fmtNum(audit.totals.reach || 0)} trend={audit.trend.reach} />
                <MetricCard label="Clicks" value={fmtNum(audit.totals.clicks)} trend={audit.trend.clicks} />
                <MetricCard label="CTR" value={`${audit.totals.ctr.toFixed(2)}%`} trend={audit.trend.ctr} />
                <MetricCard label="CPC" value={fmtMoney(audit.totals.cpc, cur)} trend={audit.trend.cpc} goodWhenDown />
                <MetricCard label="CPM" value={fmtMoney(audit.totals.cpm, cur)} trend={audit.trend.cpm} goodWhenDown />
                <MetricCard label="Freq." value={(audit.totals.frequency || 0).toFixed(2)} trend={audit.trend.frequency} goodWhenDown />
              </div>

              {audit.conversions && audit.conversions.length > 0 && (
                <ConversionsPanel conversions={audit.conversions} primaryId={audit.primaryConversion?.id} cur={cur} />
              )}

              {bundle && bundle.insights.length > 0 && <SmartInsights insights={bundle.insights} />}

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div className="glass-card p-5">
                  <h3 className="text-sm font-semibold text-white mb-3">Findings <span className="text-gray-600">· {audit.findings.length}</span></h3>
                  <div className="space-y-2.5">
                    {audit.findings.map((f, i) => {
                      const s = FSEV[f.severity]; const Icon = s.icon;
                      return (
                        <div key={i} className={`border-l-2 ${s.border} bg-white/[0.02] rounded-r-lg pl-3 pr-3 py-2`}>
                          <div className="flex items-center gap-2 mb-0.5">
                            <Icon className={`w-3.5 h-3.5 ${s.color} shrink-0`} />
                            <span className="text-sm text-gray-200 font-medium">{f.title}</span>
                          </div>
                          <p className="text-xs text-gray-500 leading-relaxed">{f.detail}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="glass-card p-5">
                  <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4 text-amber-400" /> Recommendations <span className="text-gray-600">· {audit.recommendations.length}</span>
                  </h3>
                  <div className="space-y-2.5">
                    {audit.recommendations.length === 0 && <p className="text-xs text-gray-500">No actions — account looks healthy.</p>}
                    {audit.recommendations.map((r, i) => (
                      <div key={i} className="bg-white/[0.02] rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded font-semibold ${
                            r.priority === 'high' ? 'bg-red-500/15 text-red-400'
                              : r.priority === 'medium' ? 'bg-amber-500/15 text-amber-400'
                              : 'bg-gray-500/15 text-gray-400'}`}>{r.priority}</span>
                          <span className="text-sm text-gray-200 font-medium">{r.title}</span>
                        </div>
                        <p className="text-xs text-gray-500 leading-relaxed">{r.detail}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="glass-card p-5">
                <h3 className="text-sm font-semibold text-white mb-3">Benchmarks <span className="text-gray-600 text-xs font-normal">· general reference</span></h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {audit.benchmarks.map(b => (
                    <div key={b.metric} className="rounded-lg border border-white/[0.08] bg-[rgb(var(--background))]/40 p-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs text-gray-400">{b.metric}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded ${
                          b.verdict === 'above' ? 'bg-green-500/15 text-green-400'
                            : b.verdict === 'below' ? 'bg-red-500/15 text-red-400'
                            : 'bg-gray-500/15 text-gray-400'}`}>{b.verdict}</span>
                      </div>
                      <span className="text-lg font-bold text-white">{b.metric === 'CTR' ? `${b.you}%` : b.unit && b.unit.length > 1 ? `${b.unit} ${b.you}` : `${b.you}${b.unit}`}</span>
                      <p className="text-[10px] text-gray-600">ref {b.metric === 'CTR' ? `${b.reference}%` : `${b.reference}${b.unit && b.unit.length > 1 ? '' : b.unit}`}</p>
                    </div>
                  ))}
                </div>
              </div>

              {audit.notes.length > 0 && (
                <div className="rounded-xl border border-white/[0.08] bg-white/[0.04] p-4">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wide mb-2">Notes & caveats</p>
                  <ul className="space-y-1">
                    {audit.notes.map((n, i) => <li key={i} className="text-xs text-gray-500 flex gap-2"><span className="text-gray-700">•</span>{n}</li>)}
                  </ul>
                  <p className="text-[10px] text-gray-600 mt-3">Generated {new Date(audit.generatedAt).toLocaleString()} · {audit.source === 'graph-live' ? 'Meta Graph API' : 'meta-ads MCP snapshot'}</p>
                </div>
              )}
            </div>
          )}

          {/* ===== TRENDS ===== */}
          {tab === 'trends' && (
            bundle && bundle.timeseries.length
              ? <TrendCharts data={bundle.timeseries} currency={cur} />
              : <BundlePending loading={bundleLoading} hasToken={liveRefresh} />
          )}

          {/* ===== AUDIENCE ===== */}
          {tab === 'audience' && (
            bundle
              ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <BreakdownPanel title="By age" rows={bundle.byAge} currency={cur} />
                    <BreakdownPanel title="By gender" rows={bundle.byGender} currency={cur} />
                  </div>
                  <BreakdownPanel title="By country" rows={bundle.byCountry} currency={cur} kind="country" max={15} />
                </div>
              )
              : <BundlePending loading={bundleLoading} hasToken={liveRefresh} />
          )}

          {/* ===== DELIVERY ===== */}
          {tab === 'delivery' && (
            bundle
              ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <BreakdownPanel title="By platform" rows={bundle.byPlatform} currency={cur} />
                    <BreakdownPanel title="By device" rows={bundle.byDevice} currency={cur} />
                  </div>
                  <BreakdownPanel title="By placement position" rows={bundle.byPosition} currency={cur} />
                </div>
              )
              : <BundlePending loading={bundleLoading} hasToken={liveRefresh} />
          )}

          {/* ===== CAMPAIGNS ===== */}
          {tab === 'campaigns' && (
            <div className="space-y-4">
              <EntityTable title="Campaigns" rows={audit.campaigns} cur={cur} conv={audit.primaryConversion?.name} />
              <EntityTable title="Ad sets" rows={audit.adsets} cur={cur} conv={audit.primaryConversion?.name} />
              <EntityTable title="Ads" rows={audit.ads} cur={cur} conv={audit.primaryConversion?.name} />
            </div>
          )}

          {/* ===== TRACKER ===== */}
          {tab === 'tracker' && <TrackerGrid accountId={selected} campaign={campaign} currency={cur} liveRefresh={liveRefresh} />}
        </>
      )}

      {/* ===== ASSISTANT ===== (available even without a saved audit) */}
      {!loading && selected && tab === 'assistant' && (
        <AdsAssistant
          accountId={selected}
          accountName={acct?.name || selected}
          currency={cur}
          audit={audit}
          rangeLabel={isCustom ? `${customSince} → ${customUntil}` : (RANGE_LABEL[range] || range)}
        />
      )}
    </div>
  );
}
